import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.348],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

/** `2026-08-10T…` → `3 minutes ago`. Absolute time stays in the tooltip. */
export function formatRelative(date: Date, now: Date = new Date()): string {
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  let delta = (date.getTime() - now.getTime()) / 1000;

  for (const [unit, span] of UNITS) {
    if (Math.abs(delta) < span) {
      return formatter.format(Math.round(delta), unit);
    }
    delta /= span;
  }
  return formatter.format(Math.round(delta), 'year');
}

export interface RelativeTimeProps {
  /** An ISO timestamp, or null for "never". */
  value: string | null | undefined;
  /** What to render when the value is absent. */
  fallback?: string;
  className?: string;
}

/**
 * A timestamp a human can act on.
 *
 * The admin pages rendered raw ISO strings — `2026-08-09T22:14:07.113Z` — in
 * table cells, which is precise and useless: the question during an incident
 * is always "how long ago", never "at which UTC instant". Both are here; the
 * relative form is on screen and the absolute form is one hover away, since
 * the absolute one is what gets pasted into the incident channel.
 */
export function RelativeTime({ value, fallback = 'never', className }: RelativeTimeProps) {
  // Re-renders on a slow tick so "2 minutes ago" on an auto-refreshing page
  // does not quietly become an hour old while it sits on screen.
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(timer);
  }, []);

  if (!value) {
    return <span className={cn('text-muted-foreground', className)}>{fallback}</span>;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return <span className={cn('text-muted-foreground', className)}>unknown</span>;
  }

  return (
    <time dateTime={date.toISOString()} title={date.toLocaleString()} className={className}>
      {formatRelative(date)}
    </time>
  );
}
